import Cookies from "js-cookie";
import isUndefined from "lodash/isUndefined";
import Router from "next/router";
import { memo, useEffect, useRef } from "react";
import { UAParser } from "ua-parser-js";
import { useTrackHideIntercomWidget } from "../events/useTrackHideIntercomWidget";
import { useAnalyticsUtilities } from "../hooks/useAnalyticsUtilities";
import { getUTMParameters } from "../utils/getUTMParameters";
const getReferrer = ()=>{
    return !!document.referrer ? new URL(document.referrer) : null;
};
/** Tracks page views for analytics */ export const AnalyticsPageRouting = /*#__PURE__*/ memo((param)=>{
    let { onFirstLoad } = param;
    const isFirstLoad = useRef(true);
    const previousUrl = useRef();
    const { setGlobalEventProps } = useAnalyticsUtilities();
    const { trackHideIntercomWidget } = useTrackHideIntercomWidget();
    const onFirstLoadRef = useRef(onFirstLoad);
    onFirstLoadRef.current = onFirstLoad;
    useEffect(()=>{
        if (isFirstLoad.current) {
            var _onFirstLoadRef_current;
            // this sends a page event to intercom which is required to initialize it
            trackHideIntercomWidget();
            const parser = new UAParser();
            /**
       * Since our app is a single page app, `document.referrer` doesn't change
       * per a session. Therefore, we can utilize `document.referrer` to determine
       * the initial referrer for the user.
       */ const initialReferrer = getReferrer();
            /**
       * The initial referrer that was set in the last 60 days is used to determine
       * the initial referrer for the user. If the initial referrer is not set, then
       * we can assume that the user is a new user.
       */ const initialReferrer60DaysCookie = Cookies.get("initial-referrer-60-days");
            const initialReferrer60Days = initialReferrer60DaysCookie ? JSON.parse(initialReferrer60DaysCookie) : "$direct";
            const initialReferreringDomain60DaysCookie = Cookies.get("initial-referring-domain-60-days");
            const initialReferreringDomain60Days = initialReferreringDomain60DaysCookie ? JSON.parse(initialReferreringDomain60DaysCookie) : null;
            if (isUndefined(initialReferrer60Days)) {
                var _initialReferrer_href;
                Cookies.set("initial-referrer-60-days", JSON.stringify((_initialReferrer_href = initialReferrer === null || initialReferrer === void 0 ? void 0 : initialReferrer.href) !== null && _initialReferrer_href !== void 0 ? _initialReferrer_href : "$direct"), {
                    expires: 60
                });
            }
            if (isUndefined(initialReferreringDomain60DaysCookie)) {
                var _initialReferrer_hostname;
                Cookies.set("initial-referring-domain-60-days", JSON.stringify((_initialReferrer_hostname = initialReferrer === null || initialReferrer === void 0 ? void 0 : initialReferrer.hostname) !== null && _initialReferrer_hostname !== void 0 ? _initialReferrer_hostname : "$direct"), {
                    expires: 60
                });
            }
            /**
       * We need to set the utm parameters on the page event so
       * that they are available to the analytics for the entire
       * session and not just the initial page load.
       */ const utmParams = getUTMParameters(window.location.search);
            const initialUTMParams = getUTMParameters(window.location.search, "initial");
            /**
       * We want to track the initial UTM parameters that were
       * set when the user first visited the site.
       */ const initialUTMParamsCookie = Cookies.get("initial-utm-params");
            const initialUTMParamsCookieObject = initialUTMParamsCookie ? JSON.parse(initialUTMParamsCookie) : {};
            if (!initialUTMParamsCookie) {
                Cookies.set("initial-utm-params", JSON.stringify(initialUTMParams), {
                    expires: 60
                });
            }
            var _initialReferrer_href1, _initialReferrer_hostname1, _initialReferrer_href2, _initialReferrer_hostname2;
            setGlobalEventProps({
                OS: parser.getOS().name,
                screen_height: window.innerHeight,
                screen_width: window.innerWidth,
                initial_referrer_60_days: initialReferrer60Days,
                initial_referring_domain_60_days: initialReferreringDomain60Days,
                initial_referrer: (_initialReferrer_href1 = initialReferrer === null || initialReferrer === void 0 ? void 0 : initialReferrer.href) !== null && _initialReferrer_href1 !== void 0 ? _initialReferrer_href1 : "$direct",
                initial_referring_domain: (_initialReferrer_hostname1 = initialReferrer === null || initialReferrer === void 0 ? void 0 : initialReferrer.hostname) !== null && _initialReferrer_hostname1 !== void 0 ? _initialReferrer_hostname1 : "$direct",
                referrer: (_initialReferrer_href2 = initialReferrer === null || initialReferrer === void 0 ? void 0 : initialReferrer.href) !== null && _initialReferrer_href2 !== void 0 ? _initialReferrer_href2 : "$direct",
                referring_domain: (_initialReferrer_hostname2 = initialReferrer === null || initialReferrer === void 0 ? void 0 : initialReferrer.hostname) !== null && _initialReferrer_hostname2 !== void 0 ? _initialReferrer_hostname2 : "$direct",
                ...initialUTMParams,
                ...initialUTMParamsCookieObject,
                ...utmParams
            });
            (_onFirstLoadRef_current = onFirstLoadRef.current) === null || _onFirstLoadRef_current === void 0 ? void 0 : _onFirstLoadRef_current.call(onFirstLoadRef);
        }
        const routeChangeStartHandler = ()=>{
            previousUrl.current = Router.asPath;
        };
        const routeChangeCompleteHandler = ()=>{
            const referrer = isFirstLoad.current ? getReferrer() : new URL(window.location.protocol + window.location.hostname + previousUrl.current);
            var _referrer_href;
            setGlobalEventProps({
                referrer: (_referrer_href = referrer === null || referrer === void 0 ? void 0 : referrer.href) !== null && _referrer_href !== void 0 ? _referrer_href : "$direct"
            });
            isFirstLoad.current = false;
        };
        Router.events.on("routeChangeStart", routeChangeStartHandler);
        Router.events.on("routeChangeComplete", routeChangeCompleteHandler);
        return ()=>{
            Router.events.off("routeChangeStart", routeChangeStartHandler);
            Router.events.off("routeChangeComplete", routeChangeCompleteHandler);
        };
    }, [
        setGlobalEventProps,
        trackHideIntercomWidget
    ]);
    return null;
});
AnalyticsPageRouting.displayName = "AnalyticsPageRouting";
